import { Expose, Type } from 'class-transformer';
import {
    ArrayMaxSize,
    IsArray,
    IsBoolean,
    IsIn,
    IsInt,
    IsNumber,
    IsOptional,
    IsString,
    Max,
    MaxLength,
    Min,
    Validate,
    ValidateIf,
} from 'class-validator';

import { Languages } from '../shared/enum/languages.enum';

import { EstablishmentOfferEnum } from './enum/establishment-offer.enum';
import { EstablishmentHours, EstablishmentHoursConstraint } from './establishment-hours';
import { INTAKE_SPECIALTIES, IntakeSpecialty } from './establishment-specialties';

/** Closed answers a venue can confirm. The guide pitch is not one of them. */
export const INTAKE_CUISINES = [
    'thai',
    'isan',
    'chinese',
    'indian',
    'israeli',
    'japanese',
    'italian',
    'french',
    'mexican',
    'bakery',
    'seafood',
    'vegetarian',
    'street-food',
] as const;

export const INTAKE_DRINKS = [
    'cocktails',
    'craft-beer',
    'natural-wine',
    'gin',
    'agave',
    'roaster',
] as const;

export const INTAKE_ROOMS = ['terrace', 'rooftop', 'upstairs', 'counter', 'listening'] as const;

export const INTAKE_PETS = ['no', 'yes', 'outside'] as const;

/** `restricted` means the legal drinking age of the place's country, filled in by the server. */
export const INTAKE_AGES = ['all', 'restricted'] as const;

export const INTAKE_BOOKINGS = ['walk-in', 'helps', 'required'] as const;

export type IntakeCuisine = (typeof INTAKE_CUISINES)[number];
export type IntakeDrink = (typeof INTAKE_DRINKS)[number];
export type IntakeRoom = (typeof INTAKE_ROOMS)[number];
export type IntakePets = (typeof INTAKE_PETS)[number];
export type IntakeAge = (typeof INTAKE_AGES)[number];
export type IntakeBooking = (typeof INTAKE_BOOKINGS)[number];

export const OWNER_CONFIRM = ['unconfirmed', 'link_sent', 'confirmed'] as const;
export type OwnerConfirm = (typeof OWNER_CONFIRM)[number];

export const INTAKE_REVIEW = ['none', 'pending', 'approved', 'rejected'] as const;
export type IntakeReview = (typeof INTAKE_REVIEW)[number];

export type EstablishmentIntakeProfile = {
    cuisines: IntakeCuisine[];
    drinks: IntakeDrink[];
    rooms: IntakeRoom[];
    pets: IntakePets | null;
    age: IntakeAge | null;
    /** Legal drinking age of the country when `age` is `restricted`. The form does not send this. */
    minAge: number | null;
    booking: IntakeBooking | null;
    cashOnly: boolean | null;
    /** Name printed under the photos, once we have permission to use them. */
    photoCredit: string | null;
    /** Plates a guest can ask for. Two only when the room is honestly both. */
    specialties: IntakeSpecialty[];
};

/** One owner submission. Live fields stay as they are until an admin publishes the pack. */
export type EstablishmentIntakeBatch = {
    submittedAt: string;
    title: string;
    offers: EstablishmentOfferEnum[];
    hours: EstablishmentHours | null;
    hoursNote: string | null;
    address: string | null;
    lat: number;
    lng: number;
    instagram: string | null;
    website: string | null;
    phone: string | null;
    whatsapp: string | null;
    facebook: string | null;
    line: string | null;
    operator: string | null;
    openedYear: number | null;
    priceLevel: number | null;
    photos: string[];
    coverPhotoIndex: number | null;
    closedNote: string | null;
    profile: EstablishmentIntakeProfile;
};

export type IntakeMarket = {
    currency: string;
    /** Upper edge of price levels 1, 2 and 3. Level 4 is anything above the last. */
    bands: [number, number, number];
    drinkingAge: number;
};

export class EstablishmentIntakeQueryDto {
    @IsString()
    @MaxLength(2000)
    @Expose()
    token: string;

    @IsString()
    @MaxLength(80)
    @Expose()
    city: string;

    @IsString()
    @MaxLength(160)
    @Expose()
    place: string;

    /** Language of the dish list. Omitted by older forms. */
    @IsOptional()
    @IsIn(Object.values(Languages))
    @Expose()
    language?: Languages;
}

export class EstablishmentIntakeDto extends EstablishmentIntakeQueryDto {
    @IsString()
    @MaxLength(180)
    @Expose()
    title: string;

    @IsArray()
    @IsIn(Object.values(EstablishmentOfferEnum), { each: true })
    @Expose()
    offers: EstablishmentOfferEnum[];

    @IsOptional()
    @Validate(EstablishmentHoursConstraint)
    @Expose()
    hours?: EstablishmentHours | null;

    @IsOptional()
    @IsString()
    @MaxLength(400)
    @Expose()
    hoursNote?: string | null;

    @IsOptional()
    @IsString()
    @MaxLength(500)
    @Expose()
    address?: string | null;

    @Type(() => Number)
    @IsNumber()
    @Min(-90)
    @Max(90)
    @Expose()
    lat: number;

    @Type(() => Number)
    @IsNumber()
    @Min(-180)
    @Max(180)
    @Expose()
    lng: number;

    @IsOptional()
    @IsString()
    @MaxLength(300)
    @Expose()
    instagram?: string | null;

    @IsOptional()
    @IsString()
    @MaxLength(500)
    @Expose()
    website?: string | null;

    @IsOptional()
    @IsString()
    @MaxLength(40)
    @Expose()
    phone?: string | null;

    @IsOptional()
    @IsString()
    @MaxLength(300)
    @Expose()
    whatsapp?: string | null;

    @IsOptional()
    @IsString()
    @MaxLength(500)
    @Expose()
    facebook?: string | null;

    @IsOptional()
    @IsString()
    @MaxLength(300)
    @Expose()
    line?: string | null;

    @IsOptional()
    @IsString()
    @MaxLength(200)
    @Expose()
    operator?: string | null;

    @IsOptional()
    @ValidateIf((_, value) => value !== null && value !== undefined)
    @Type(() => Number)
    @IsInt()
    @Min(1800)
    @Max(2100)
    @Expose()
    openedYear?: number | null;

    @IsOptional()
    @ValidateIf((_, value) => value !== null && value !== undefined)
    @Type(() => Number)
    @IsInt()
    @Min(1)
    @Max(4)
    @Expose()
    priceLevel?: number | null;

    @IsArray()
    @ArrayMaxSize(8)
    @IsString({ each: true })
    @MaxLength(500, { each: true })
    @Expose()
    photos: string[];

    @IsOptional()
    @ValidateIf((_, value) => value !== null && value !== undefined)
    @Type(() => Number)
    @IsInt()
    @Min(0)
    @Expose()
    coverPhotoIndex?: number | null;

    @IsOptional()
    @IsString()
    @MaxLength(500)
    @Expose()
    closedNote?: string | null;

    @IsArray()
    @ArrayMaxSize(2)
    @IsIn(INTAKE_CUISINES, { each: true })
    @Expose()
    cuisines: IntakeCuisine[];

    @IsArray()
    @ArrayMaxSize(2)
    @IsIn(INTAKE_DRINKS, { each: true })
    @Expose()
    drinks: IntakeDrink[];

    @IsArray()
    @ArrayMaxSize(2)
    @IsIn(INTAKE_ROOMS, { each: true })
    @Expose()
    rooms: IntakeRoom[];

    @IsOptional()
    @ValidateIf((_, value) => value !== null && value !== undefined)
    @IsIn(INTAKE_PETS)
    @Expose()
    pets?: IntakePets | null;

    @IsOptional()
    @ValidateIf((_, value) => value !== null && value !== undefined)
    @IsIn(INTAKE_AGES)
    @Expose()
    age?: IntakeAge | null;

    @IsOptional()
    @ValidateIf((_, value) => value !== null && value !== undefined)
    @IsIn(INTAKE_BOOKINGS)
    @Expose()
    booking?: IntakeBooking | null;

    @IsOptional()
    @ValidateIf((_, value) => value !== null && value !== undefined)
    @IsBoolean()
    @Expose()
    cashOnly?: boolean | null;

    @IsOptional()
    @IsString()
    @MaxLength(120)
    @Expose()
    photoCredit?: string | null;

    /** Absent on older forms: the server keeps the plates already stored. */
    @IsOptional()
    @IsArray()
    @ArrayMaxSize(2)
    @IsIn(INTAKE_SPECIALTIES, { each: true })
    @Expose()
    specialties?: IntakeSpecialty[];
}

export class ReviewEstablishmentIntakeDto {
    @IsIn(['approve', 'reject'])
    @Expose()
    action: 'approve' | 'reject';
}
